// src/modal/PC/gijun/BomTree.jsx
import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AppContext } from "../../../AppStore";

// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";

// Tree (MUI Lab)
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";   // BOM 노드 아이콘
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"; // 리프(자재) 아이콘

/* ------------------------ styled ------------------------ */
const Toolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 12,
}));

const Segment = styled(ButtonGroup)(({ theme }) => ({
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: theme.shadows[1],
  "& .MuiButton-root": {
    textTransform: "none",
    paddingLeft: 12,
    paddingRight: 12,
    fontWeight: 600,
  },
}));

const SegmentBtn = styled(Button)(({ theme }) => ({
  "& .MuiButton-startIcon": { marginRight: 6 },
}));

const LabelRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

/* ------------------------ label ------------------------ */
function NodeLabel({ name, count, specText, isBom }) {
  return (
    <LabelRow>
      {isBom ? (
        <WidgetsOutlinedIcon fontSize="small" />
      ) : (
        <Inventory2OutlinedIcon fontSize="small" />
      )}
      <Typography sx={{ fontWeight: 700 }}>{name ?? "-"}</Typography>

      {count !== undefined && count !== null && (
        <Chip
          size="small"
          label={`x ${count}`}
          color="default"
          sx={{ height: 22 }}
        />
      )}

      {specText && (
        <Chip
          size="small"
          variant="outlined"
          label={specText}
          sx={{ height: 22 }}
        />
      )}
    </LabelRow>
  );
}

/* ------------------------ rows → tree ------------------------ */
/**
 * rows: /api/p/Bom/TreeList 결과 (배열)
 * 각 행 예: { level, bom_id, bom_version_seq, parent_bom_id, parent_ver, parent_input_seq,
 *            item_id, item_name, item_spec, item_width, item_depth, item_height, qty, has_child }
 */
function buildTree(rows, specMaker) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  // 루트 (level=0) 1건
  const rootRow = rows.find((r) => Number(r.level) === 0) ?? rows[0];

  const keyOf = (bom, ver) => `${bom ?? "null"}|${ver ?? "null"}`;
  const mkSpec = (r) =>
    specMaker?.(r?.item_spec, r?.item_width, r?.item_depth, r?.item_height) ??
    "";

  // bom_id|ver → 노드
  const bomMap = new Map();

  const makeNode = ({ nodeId, row, isBom }) => ({
    nodeId: String(nodeId),
    name: row?.item_name ?? "-",
    count: row?.qty ?? row?.item_count ?? null,
    specText: mkSpec(row),
    isBom: !!isBom,
    children: [],
  });

  // 1) 루트 등록
  const rootKey = keyOf(rootRow.bom_id, rootRow.bom_version_seq);
  const rootNode = makeNode({ nodeId: `bom:${rootKey}`, row: rootRow, isBom: true });
  bomMap.set(rootKey, rootNode);

  // 2) 나머지 행을 부모에 부착
  for (const r of rows) {
    if (Number(r.level) === 0) continue;

    const parentKey = keyOf(r.parent_bom_id, r.parent_ver);
    const parent = bomMap.get(parentKey);
    if (!parent) continue;

    if (r.bom_id != null) {
      const childKey = keyOf(r.bom_id, r.bom_version_seq);
      let child = bomMap.get(childKey);
      if (!child) {
        child = makeNode({ nodeId: `bom:${childKey}`, row: r, isBom: true });
        bomMap.set(childKey, child);
      }
      parent.children.push(child);
    } else {
      const leafId = `leaf:${parentKey}:${r.parent_input_seq}`;
      parent.children.push(makeNode({ nodeId: leafId, row: r, isBom: false }));
    }
  }

  return rootNode;
}

/* ------------------------ component ------------------------ */
export default function BomTree({ bomId, panelHeight = 400 }) {
  const appStore = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [root, setRoot] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);

  const fetchTree = async () => {
    if (!bomId) return;
    try {
      setLoading(true);
      const res = await axios.get("/api/p/Bom/TreeList", {
        params: { bom_id: bomId },
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : [];

      const tree = buildTree(rows, appStore?.ITEM_FULL_SPEC_MAKE);
      if (!tree) {
        Swal.fire({
          title: "<p style='color:red'>BOM 트리 조회 실패</p>",
          text: "결과가 비어 있습니다.",
          confirmButtonText: "확인",
        });
        setRoot(null);
        setExpanded([]);
        setSelected([]);
        return;
      }

      setRoot(tree);

      // 전체 펼치기
      const all = [];
      const walk = (n) => {
        all.push(n.nodeId);
        (n.children || []).forEach(walk);
      };
      walk(tree);
      setExpanded(all);
      setSelected([tree.nodeId]);
    } catch (e) {
      // 서버 에러 수집 훅
      appStore?.capture?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomId]);

  const handleExpandAll = () => {
    if (!root) return;
    const all = [];
    const walk = (n) => {
      all.push(n.nodeId);
      (n.children || []).forEach(walk);
    };
    walk(root);
    setExpanded(all);
  };

  const handleCollapseAll = () => setExpanded([]);

  const renderNode = (n) => (
    <TreeItem
      key={n.nodeId}
      nodeId={n.nodeId}
      label={<NodeLabel name={n.name} count={n.count} specText={n.specText} isBom={n.isBom} />}
    >
      {(n.children || []).map(renderNode)}
    </TreeItem>
  );

  return (
    <Box sx={{ height: panelHeight, overflowY: "auto", px: 2, pt: 1.25 }}>
      {/* segmented toolbar */}
      <Toolbar>
        <Segment variant="outlined">
          <SegmentBtn
            onClick={handleExpandAll}
            startIcon={<UnfoldMoreIcon />}
            disabled={loading || !root}
          >
            전체 펼치기
          </SegmentBtn>
          <SegmentBtn
            onClick={handleCollapseAll}
            startIcon={<UnfoldLessIcon />}
            disabled={loading || !root}
          >
            전체 접기
          </SegmentBtn>
          <SegmentBtn
            onClick={fetchTree}
            startIcon={<RefreshIcon />}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            새로고침
          </SegmentBtn>
        </Segment>
      </Toolbar>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : root ? (
        <TreeView
          expanded={expanded}
          selected={selected}
          onNodeToggle={(_, ids) => setExpanded(ids)}
          onNodeSelect={(_, ids) => setSelected(Array.isArray(ids) ? ids : [ids])}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={{
            pr: 1,
            "& .MuiTreeItem-content": {
              borderRadius: 8,
              py: 0.25,
              "&.Mui-selected": {
                backgroundColor: (t) => t.palette.action.selected,
              },
              "&:hover": {
                backgroundColor: (t) => t.palette.action.hover,
              },
            },
            "& .MuiTreeItem-label": { py: 0.25 },
          }}
        >
          {renderNode(root)}
        </TreeView>
      ) : (
        <Typography sx={{ opacity: 0.7 }}>데이터가 없습니다.</Typography>
      )}
    </Box>
  );
}
